import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ResourceArticle} from '../../../model/resource';
import {ResourcesService} from '../../../services/resources.service';
import {HttpEventType} from '@angular/common/http';
import {CommonModule} from "@angular/common";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {ResourceDownloadIndicatorComponent} from "../resource-download-indicator/resource-download-indicator.component";

@Component({
    selector: 'app-resources-tree',
    imports: [
        CommonModule,
        FaIconComponent,
        ResourceDownloadIndicatorComponent,
    ],
    templateUrl: './resources-tree.component.html',
    styleUrls: ['./resources-tree.component.css']
})
export class ResourcesTreeComponent implements OnInit {
  @Output()
  blob = new EventEmitter<{ blob: Blob, filename: string }>();
  @Output()
  loading = new EventEmitter<boolean>();
  @Input() deepness: number;
  @Input()
  set resources(res: ResourceArticle[]) {
    this._resources = res;
    const newcat = this.getCategories().map(c => {
      return {title: c, display: !!this._search};
    }).sort((c1, c2) => c1.title.toLowerCase().localeCompare(c2.title.toLowerCase()));
    this.categories.splice(0, this.categories.length, ...newcat);

    const newarticles = this.getArticles().sort((a1, a2) => a1.title.toLowerCase().localeCompare(a2.title.toLowerCase()));
    this.articles.splice(0, this.articles.length, ...newarticles);

    this.updateReadIndicator();
  }
  @Input() category: string;
  @Input()
  set search(str: string) {
    // Refresh
    this._search = str;
  }
  _search: string;

  _resources: ResourceArticle[];
  get resources(): ResourceArticle[] {
    return this._resources;
  }

  @Input() set showall(bool: boolean) {
    if (bool) {
      this.categories.forEach(c => {
        c.display = true;
      });
      this.$showAll = true;
      setTimeout(() => {
        this.$showAll = undefined;
      });
    }
  }

  @Input() set hideall(bool: boolean) {
    if (bool) {
      this.categories.forEach(c => {
        c.display = false;
      });
      this.$hideAll = true;
      setTimeout(() => {
        this.$hideAll = undefined;
      });
    }
  }

  SEPARATOR = '::';
  categories: {
    title: string,
    display: boolean,
    read?: boolean
  }[] = [];
  articles: ResourceArticle[] = [];
  downloads = {};
  $showAll = undefined;
  $hideAll = undefined;

  iconsTypes = {
    'application/pdf': 'file-pdf',
    'application/x-pdf': 'file-pdf',
    'application/zip': 'file-archive',
    'application/x-zip-compressed': 'file-archive',
    'application/zip-compressed': 'file-archive'
  };

  constructor(private resourcesService: ResourcesService, private cdr: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.categories = this.getCategories().map(c => {
      return {title: c, display: !!this._search};
    }).sort((c1, c2) => c1.title.toLowerCase().localeCompare(c2.title.toLowerCase()));
    this.articles = this.getArticles().sort((a1, a2) => a1.title.toLowerCase().localeCompare(a2.title.toLowerCase()));
    this.updateReadIndicator();
  }

  getCategories(): string[] {
    if (this.category) {
      return this.getSubCategories(this.category);
    } else {
      return this.getMainCategories();
    }
  }

  updateReadIndicator(): void {
    // Check for article in sub article
    this.categories.map(c => {
      c.read = this.resources.find(ra => {
        return ra.category.indexOf(c.title) !== -1 && !ra.read;
      }) !== undefined;
      return c;
    })
  }

  getArticles(): ResourceArticle[] {
    return this.resources.filter(r => {
      return r.category === this.category;
    });
  }

  getMainCategories(): string[] {
    return [...new Set(this.resources.map(r => r.category.split(this.SEPARATOR)[0]))];
  }

  getSubCategories(main: string): string[] {
    const toJump = main.split(this.SEPARATOR).length;
    return [...new Set(this.resources
      .filter(r => r.category.startsWith(main + this.SEPARATOR))
      .map(r => r.category.split(this.SEPARATOR)[toJump]))];
  }

  downloadFile(event, id: string, attachment: ResourceArticle, forceDownload: boolean = false): void {
    event.stopPropagation();
    event.preventDefault();
    event.stopImmediatePropagation();
    attachment.read = true;
    this.updateReadIndicator();
    if (this.downloads[id] === undefined) {
      this.downloads[id] = 0;
      if (attachment.filename.toLowerCase().endsWith(".pdf") && !forceDownload) {
        this.loading.emit(true);
      } else {
        this.blob.emit({blob: null, filename: null});
      }
      this.resourcesService.downloadAttachment(id).subscribe(data => {
        if (data.type === HttpEventType.DownloadProgress) {
          this.downloads[id] = Math.round(data.loaded / attachment.filesize * 100);
          this.cdr.detectChanges();
        } else if (data.type === HttpEventType.Response) {
          delete this.downloads[id];
          if (attachment.filename.toLowerCase().endsWith(".pdf") && !forceDownload) {
            this.blob.emit({blob: data.body, filename: attachment.filename});
          } else {
            this.saveAs(data.body, attachment.filename);
          }
        }
      });
    }
  }

  saveAs(blob: Blob, filename: string): void {
    const downloadAncher = document.createElement("a");
    downloadAncher.style.display = "none";
    downloadAncher.href = URL.createObjectURL(blob);
    downloadAncher.download = filename;
    downloadAncher.click();
    downloadAncher.remove();
  }

  getIconFromContentType(contentType): string {
    if (Object.keys(this.iconsTypes).indexOf(contentType) !== -1) {
      return this.iconsTypes[contentType];
    } else {
      return 'file';
    }
  }

  selectFile($event: { blob: Blob, filename: string }): void {
    this.updateReadIndicator();
    this.blob.emit($event);
  }

  onChildLoading(isLoading: boolean): void {
    this.loading.emit(isLoading);
  }
}
